// ABOUTME: Modal component that blocks user access until they accept updated privacy policy and terms of service
// ABOUTME: Displays both policies with markdown rendering and requires explicit acceptance to continue using the app

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  Paper,
  Divider
} from '@mui/material';
import { getPrivacyPolicy, getTermsOfService, acknowledgePolicyDocument } from '../services/api';
import type { PolicyDocument } from '../types/api';
import { renderPolicyMarkdown } from '../utils/sanitize';

interface PolicyAcceptanceModalProps {
  open: boolean;
  onAccepted: () => void;
  onClose?: () => void; // Called when user closes modal without accepting
  onLogout?: () => void; // Called when existing user declines (should logout)
  userLastAcceptedVersions?: {
    privacyPolicy: number | null;
    termsOfService: number | null;
  };
  isExistingUser?: boolean; // If true, close button triggers logout; if false, returns to registration
  skipApiCall?: boolean; // If true, don't call acknowledgePolicyDocument API (for registration flow)
}

const PolicyAcceptanceModal: React.FC<PolicyAcceptanceModalProps> = ({
  open,
  onAccepted,
  onClose,
  onLogout,
  userLastAcceptedVersions,
  isExistingUser = false,
  skipApiCall = false
}) => {
  const [policies, setPolicies] = useState<{
    privacy: PolicyDocument | null;
    terms: PolicyDocument | null;
  }>({
    privacy: null,
    terms: null
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);

  const [readAcknowledgments, setReadAcknowledgments] = useState({
    privacy: false,
    terms: false
  });

  const [scrolledToBottom, setScrolledToBottom] = useState({
    privacy: false,
    terms: false
  });

  const loadPolicies = async () => {
    try {
      setLoading(true);
      setError(null);

      const [privacy, terms] = await Promise.all([
        getPrivacyPolicy(),
        getTermsOfService()
      ]);

      setPolicies({ privacy, terms });
    } catch {
      setError('Failed to load policies. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadPolicies();
    }
  }, [open]);

  const renderMarkdown = renderPolicyMarkdown;

  const getPolicyStatus = (policyType: 'PrivacyPolicy' | 'TermsOfService', currentVersion: number) => {
    if (!userLastAcceptedVersions) {
      return 'UPDATED';
    }

    // Convert PascalCase to camelCase for API response keys
    const camelCaseKey = policyType === 'PrivacyPolicy' ? 'privacyPolicy' : 'termsOfService';
    const lastAcceptedVersion = userLastAcceptedVersions[camelCaseKey];
    const status = lastAcceptedVersion === currentVersion ? 'Current' : 'UPDATED';
    return status;
  };

  const checkIfScrollable = (element: HTMLDivElement | null, policyType: 'privacy' | 'terms') => {
    if (!element) return;

    const { scrollHeight, clientHeight } = element;
    const needsScroll = scrollHeight > clientHeight;

    // If content doesn't need scrolling, automatically mark as scrolled to bottom
    if (!needsScroll && !scrolledToBottom[policyType]) {
      setScrolledToBottom(prev => ({
        ...prev,
        [policyType]: true
      }));
    }
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>, policyType: 'privacy' | 'terms') => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isScrolledToBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold

    if (isScrolledToBottom && !scrolledToBottom[policyType]) {
      setScrolledToBottom(prev => ({
        ...prev,
        [policyType]: true
      }));
    }
  };

  const handleAccept = async () => {
    if (!policies.privacy || !policies.terms) return;

    try {
      setAccepting(true);
      setError(null);

      // Skip API call for registration flow (user doesn't exist yet)
      if (!skipApiCall) {
        // Only accept policies that need acceptance
        const acceptancePromises = [];

        if (getPolicyStatus('PrivacyPolicy', policies.privacy.version) === 'UPDATED') {
          acceptancePromises.push(acknowledgePolicyDocument({
            documentType: 'PrivacyPolicy',
            documentVersion: policies.privacy.version
          }));
        }

        if (getPolicyStatus('TermsOfService', policies.terms.version) === 'UPDATED') {
          acceptancePromises.push(acknowledgePolicyDocument({
            documentType: 'TermsOfService',
            documentVersion: policies.terms.version
          }));
        }

        await Promise.all(acceptancePromises);
      }

      onAccepted();
    } catch {
      setError('Error accepting policies. Please try again.');
      setAccepting(false);
    }
  };

  const handleClose = () => {
    if (isExistingUser && onLogout) {
      onLogout();
    } else if (onClose) {
      onClose();
    }
  };

  const canAccept = !accepting &&
    (!policies.privacy || getPolicyStatus('PrivacyPolicy', policies.privacy.version) !== 'UPDATED' || readAcknowledgments.privacy) &&
    (!policies.terms || getPolicyStatus('TermsOfService', policies.terms.version) !== 'UPDATED' || readAcknowledgments.terms);

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      onClose={handleClose}
    >
      <DialogTitle>
        <Typography variant="h5" component="h2">
          Policy Update Required
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please review and accept our updated policies to continue using Let&apos;s Reset.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading policies...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && policies.privacy && policies.terms && (
          <Box>
            {/* Privacy Policy Section - Only show if UPDATED */}
            {getPolicyStatus('PrivacyPolicy', policies.privacy.version) === 'UPDATED' && (
              <Box sx={{ mb: 4 }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Typography variant="h6">
                  Privacy Policy
                </Typography>
                {policies.privacy.versionLabel && (
                  <Typography variant="body2" color="text.secondary">
                    {policies.privacy.versionLabel}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  color={getPolicyStatus('PrivacyPolicy', policies.privacy.version) === 'UPDATED' ? 'warning.main' : 'success.main'}
                  sx={{ fontWeight: 600 }}
                >
                  {getPolicyStatus('PrivacyPolicy', policies.privacy.version)}
                </Typography>
              </Box>

              <Paper
                ref={(el) => checkIfScrollable(el, 'privacy')}
                sx={{ p: 2, maxHeight: 300, overflow: 'auto', mb: 2 }}
                onScroll={(e) => handleScroll(e, 'privacy')}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: renderMarkdown(policies.privacy.content)
                  }}
                />
              </Paper>

              {getPolicyStatus('PrivacyPolicy', policies.privacy.version) === 'UPDATED' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={readAcknowledgments.privacy}
                      disabled={!scrolledToBottom.privacy}
                      onChange={(e) => setReadAcknowledgments(prev => ({
                        ...prev,
                        privacy: e.target.checked
                      }))}
                    />
                  }
                  label={
                    scrolledToBottom.privacy
                      ? "I have read and understand the Privacy Policy"
                      : "Please scroll to the bottom to read the full Privacy Policy"
                  }
                />
              )}
              </Box>
            )}

            {/* Show divider only if both policies are updated */}
            {getPolicyStatus('PrivacyPolicy', policies.privacy.version) === 'UPDATED' &&
             getPolicyStatus('TermsOfService', policies.terms.version) === 'UPDATED' && (
              <Divider sx={{ my: 3 }} />
            )}

            {/* Terms of Service Section - Only show if UPDATED */}
            {getPolicyStatus('TermsOfService', policies.terms.version) === 'UPDATED' && (
              <Box sx={{ mb: 4 }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Typography variant="h6">
                  Terms of Service
                </Typography>
                {policies.terms.versionLabel && (
                  <Typography variant="body2" color="text.secondary">
                    {policies.terms.versionLabel}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  color={getPolicyStatus('TermsOfService', policies.terms.version) === 'UPDATED' ? 'warning.main' : 'success.main'}
                  sx={{ fontWeight: 600 }}
                >
                  {getPolicyStatus('TermsOfService', policies.terms.version)}
                </Typography>
              </Box>

              <Paper
                ref={(el) => checkIfScrollable(el, 'terms')}
                sx={{ p: 2, maxHeight: 300, overflow: 'auto', mb: 2 }}
                onScroll={(e) => handleScroll(e, 'terms')}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: renderMarkdown(policies.terms.content)
                  }}
                />
              </Paper>

              {getPolicyStatus('TermsOfService', policies.terms.version) === 'UPDATED' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={readAcknowledgments.terms}
                      disabled={!scrolledToBottom.terms}
                      onChange={(e) => setReadAcknowledgments(prev => ({
                        ...prev,
                        terms: e.target.checked
                      }))}
                    />
                  }
                  label={
                    scrolledToBottom.terms
                      ? "I have read and understand the Terms of Service"
                      : "Please scroll to the bottom to read the full Terms of Service"
                  }
                />
              )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          onClick={handleClose}
          size="large"
        >
          {isExistingUser ? 'Logout' : 'Cancel'}
        </Button>
        <Button
          variant="contained"
          onClick={handleAccept}
          disabled={!canAccept}
          size="large"
        >
          {accepting ? 'Accepting...' : 'Accept'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PolicyAcceptanceModal;